import { sortOptions } from "@/mocks/filters";

export interface FilterState {
  region: string;
  year: string;
  genre: string;
  sort: string;
}

interface FilterBarProps {
  filters: FilterState;
  regionOptions: string[];
  yearOptions: string[];
  genreOptions: string[];
  onChange: (next: Partial<FilterState>) => void;
  onReset: () => void;
}

function FilterGroup({
  label,
  options,
  value,
  onSelect,
}: {
  label: string;
  options: string[];
  value: string;
  onSelect: (option: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200 py-3 lg:flex-row lg:items-start lg:gap-4">
      <span className="flex h-7 shrink-0 items-center text-sm font-medium text-foreground-500 lg:w-16">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = value === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onSelect(option)}
              className={`whitespace-nowrap rounded-md px-3 py-1 text-sm transition-colors ${
                active
                  ? "bg-primary-600 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterBar({
  filters,
  regionOptions,
  yearOptions,
  genreOptions,
  onChange,
  onReset,
}: FilterBarProps) {
  const hasActive =
    filters.region !== "全部" ||
    filters.year !== "全部" ||
    filters.genre !== "全部" ||
    filters.sort !== "popular";

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="mb-1 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900">
          <i className="ri-filter-3-line text-primary-700"></i>
          筛选条件
        </span>
        {hasActive && (
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1 whitespace-nowrap text-xs text-accent-600 transition-colors hover:text-accent-700"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        )}
      </div>
      <FilterGroup
        label="地区"
        options={regionOptions}
        value={filters.region}
        onSelect={(region) => onChange({ region })}
      />
      <FilterGroup
        label="年份"
        options={yearOptions}
        value={filters.year}
        onSelect={(year) => onChange({ year })}
      />
      <FilterGroup
        label="类型"
        options={genreOptions}
        value={filters.genre}
        onSelect={(genre) => onChange({ genre })}
      />
      <div className="flex flex-col gap-2 pt-3 lg:flex-row lg:items-center lg:gap-4">
        <span className="flex h-7 shrink-0 items-center text-sm font-medium text-foreground-500 lg:w-16">
          排序
        </span>
        <div className="flex flex-wrap gap-2">
          {sortOptions.map((option) => {
            const active = filters.sort === option.key;
            return (
              <button
                key={option.key}
                type="button"
                onClick={() => onChange({ sort: option.key })}
                className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1 text-sm transition-colors ${
                  active
                    ? "bg-accent-500 text-primary-950"
                    : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
                }`}
              >
                {active && <i className="ri-check-line text-xs"></i>}
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}